import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import TenantList from "../../components/admin/TenantList";
import { fetchTenants } from "../../api/tenantApi";
import { listBusinesses } from "../../api/chatbotApi";
import useAuth from "../../hooks/useAuth";
import { isPlatformAdmin, isTenantAdmin } from "../../util/accessControl";
import LoadingState from "../../components/admin/LoadingState";

const TenantListPage = () => {
	const { user } = useAuth();
	const [tenants, setTenants] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");

	const myTenantId = user?.tenantId;

	const loadAllTenants = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const bots = await listBusinesses();
			setTenants(bots);
		} catch (fetchError) {
			setError(fetchError.message || "Failed to load tenants.");
		} finally {
			setLoading(false);
		}
	}, []);

	const loadMyTenant = useCallback(async () => {
		setLoading(true);
		try {
			const response = await fetchTenants();
			const items = response?.data?.items || response?.data || response || [];
			const allTenants = Array.isArray(items) ? items : [];
			const myTenant = allTenants.find((t) => String(t.id) === String(myTenantId));
			setTenants(myTenant ? [myTenant] : []);
		} catch (fetchError) {
			setError(fetchError.message || "Failed to load tenant.");
		} finally {
			setLoading(false);
		}
	}, [myTenantId]);

	useEffect(() => {
		if (isPlatformAdmin(user)) {
			loadAllTenants();
		} else if (myTenantId) {
			loadMyTenant();
		} else {
			loadAllTenants();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isSingleTenantView = !isPlatformAdmin(user) && tenants.length <= 1;
	if (loading) return <LoadingState label="Loading tenants..." />;

	// Tenant admins load their accessible tenants before continuing to their own bot.
	if (isTenantAdmin(user) && myTenantId) {
		return <Navigate to={`/bots/${myTenantId}`} replace />;
	}

	return (
		<TenantList
			tenants={tenants}
			loading={loading}
			error={error}
			search={search}
			statusFilter={statusFilter}
			onSearchChange={setSearch}
			onStatusChange={setStatusFilter}
			onClearFilters={() => {
				setSearch("");
				setStatusFilter("all");
			}}
			onRetry={isPlatformAdmin(user) ? loadAllTenants : loadMyTenant}
			singleTenantMode={isSingleTenantView}
		/>
	);
};

export default TenantListPage;
